import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { GigmaWordmark } from "../components/Logo";
import { WizardProgress } from "../components/WizardProgress";
import { GooglePreviewCard } from "../components/GooglePreviewCard";
import BusinessInfoStep from "../components/steps/BusinessInfoStep";
import BusinessTypeStep from "../components/steps/BusinessTypeStep";
import ServicesStep from "../components/steps/ServicesStep";
import HoursStep from "../components/steps/HoursStep";
import PhotosStep from "../components/steps/PhotosStep";
import PreviewStep from "../components/steps/PreviewStep";
import ReviewStep from "../components/steps/ReviewStep";
import PaymentStep from "../components/steps/PaymentStep";

const TOTAL = 8;

export default function Wizard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = Math.max(1, Math.min(TOTAL, parseInt(params.get("step") || "1", 10)));
  const [step, setStep] = useState(initial);

  // Sync URL <-> state
  useEffect(() => {
    setParams({ step: String(step) }, { replace: true });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, setParams]);

  const goBack = () => {
    if (step === 1) {
      navigate("/");
      return;
    }
    setStep((s) => Math.max(1, s - 1));
  };
  const goNext = () => setStep((s) => Math.min(TOTAL, s + 1));
  const goTo = (s) => setStep(Math.max(1, Math.min(TOTAL, s)));

  const renderStepBody = () => {
    switch (step) {
      case 1: return <BusinessInfoStep />;
      case 2: return <BusinessTypeStep />;
      case 3: return <ServicesStep />;
      case 4: return <HoursStep />;
      case 5: return <PhotosStep />;
      case 6: return <PreviewStep />;
      case 7: return <ReviewStep onEdit={goTo} />;
      case 8: return <PaymentStep />;
      default: return null;
    }
  };

  const isReview = step === 7;
  const isPayment = step === 8;
  const nextLabel = isReview ? "Approve setup" : "Continue";

  return (
    <div className="min-h-screen bg-slate-50/60">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="header-home">
            <GigmaWordmark size={28} />
          </Link>
          <Link
            to="/"
            data-testid="exit-wizard"
            className="text-xs text-slate-500 hover:text-slate-800"
          >
            Save & exit
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 md:py-10">
        <WizardProgress step={step} />

        <div className="mt-8 grid lg:grid-cols-12 gap-8">
          {/* Form column */}
          <div className="lg:col-span-7" key={step}>
            <div className="rounded-3xl bg-white border border-slate-200 p-6 md:p-8 gigma-card-shadow">
              <div className="step-enter step-enter-active">
                {renderStepBody()}
              </div>

              {/* Nav buttons */}
              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
                <button
                  onClick={goBack}
                  data-testid="back-button"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-full px-4 py-2 hover:bg-slate-100 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>

                {!isPayment && (
                  <button
                    onClick={goNext}
                    data-testid="next-button"
                    className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-5 py-2.5 transition-all ${
                      isReview
                        ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20"
                        : "bg-slate-900 text-white hover:-translate-y-0.5"
                    }`}
                  >
                    {isReview && <Check className="w-4 h-4" />}
                    {nextLabel} {!isReview && <ArrowRight className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Preview sidebar (sticky on desktop) */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 gigma-live-dot" />
                Live Google preview
              </div>
              <GooglePreviewCard />
              <p className="text-xs text-slate-400 mt-3 leading-relaxed px-1">
                Simulates how you’ll appear on Google Search. Updates as you type.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
